import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiOpenInNew } from '@mdi/js'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { useAdminSettingOrganizations } from '../../hooks/use-admin-settings'
import { EmptyState } from '../ui/empty-state'

interface DrilldownProps {
    code: string | null
    /**
     * The code's kind, if known — determines whether the per-org row links
     * out to that organization's settings page. Only flag-kind codes have a
     * dedicated org-scoped page today (/{org}/settings/feature-flags);
     * setting-kind codes (locale, webhook_url, ...) have no equivalent page
     * yet, so their rows render the organization name/code as plain text
     * rather than a misleading link into the flags page.
     */
    kind?: 'flag' | 'setting'
    onClose: () => void
}

/**
 * SettingsRegistryDrilldown is a read-only Sheet listing every organization
 * holding a stored value for `code` (GET /admin/settings/{code}/organizations,
 * spec 2026-07-04-12-super-admin-settings-registry-ui). Org values are edited
 * from the organization's own settings context — this view never writes.
 */
export function SettingsRegistryDrilldown({ code, kind, onClose }: DrilldownProps) {
    const { t, i18n } = useTranslation('admin')
    const { data, isLoading, isError } = useAdminSettingOrganizations(code)
    const entries = (data as { data?: Array<{
        organization: { code: string; name: string }
        value: unknown
        updated_at: string
        updated_by: { uid: string; name: string } | null
    }> })?.data ?? []

    return (
        <Sheet open={!!code} onOpenChange={(open) => { if (!open) onClose() }}>
            <SheetContent side="right" className="sm:max-w-xl" data-testid="settings-registry-drilldown">
                <SheetHeader>
                    <SheetTitle>{code ? t('registry.drilldown.title', { code }) : ''}</SheetTitle>
                    <SheetDescription>{t('registry.drilldown.description')}</SheetDescription>
                </SheetHeader>

                <div className="flex flex-col gap-4 overflow-y-auto px-4 pb-4">
                    {isLoading && (
                        <div className="text-sm text-muted-foreground text-center py-6" data-testid="settings-registry-drilldown-loading">
                            {t('registry.drilldown.loading')}
                        </div>
                    )}
                    {isError && (
                        <div className="text-sm text-destructive text-center py-6" data-testid="settings-registry-drilldown-error">
                            {t('registry.drilldown.error')}
                        </div>
                    )}
                    {!isLoading && !isError && entries.length === 0 && (
                        <EmptyState
                            variant="inline"
                            live
                            title={t('registry.drilldown.empty')}
                            data-testid="settings-registry-drilldown-empty"
                        />
                    )}
                    {!isLoading && !isError && entries.length > 0 && (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('registry.drilldown.column_org')}</TableHead>
                                        <TableHead>{t('registry.drilldown.column_value')}</TableHead>
                                        <TableHead>{t('registry.drilldown.column_updated_at')}</TableHead>
                                        <TableHead>{t('registry.drilldown.column_updated_by')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {entries.map((e) => (
                                        <TableRow key={e.organization.code} data-testid={`settings-registry-drilldown-row-${e.organization.code}`}>
                                            <TableCell>
                                                {kind === 'flag' ? (
                                                    <Link
                                                        to="/$org/settings/feature-flags"
                                                        params={{ org: e.organization.code }}
                                                        className="inline-flex items-center gap-1 text-primary hover:underline"
                                                        data-testid={`settings-registry-drilldown-org-link-${e.organization.code}`}
                                                        title={t('registry.drilldown.open_org_settings')}
                                                    >
                                                        {e.organization.name || e.organization.code}
                                                        <AppIcon path={mdiOpenInNew} size="xs" />
                                                    </Link>
                                                ) : (
                                                    <span data-testid={`settings-registry-drilldown-org-${e.organization.code}`}>
                                                        {e.organization.name || e.organization.code}
                                                    </span>
                                                )}
                                                <div className="text-xs font-mono text-muted-foreground">{e.organization.code}</div>
                                            </TableCell>
                                            <TableCell>
                                                <code className="text-xs">{JSON.stringify(e.value)}</code>
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {new Date(e.updated_at).toLocaleString(i18n.language)}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {e.updated_by?.name ?? t('registry.drilldown.system_actor')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    )
}
